import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface ContactInfo {
  name?: string;
  address?: string;
  nmls?: string;
  stateLicense?: string;
  contactName?: string;
  contactNmls?: string;
  contactStateLicense?: string;
  email?: string;
  phone?: string;
}

export interface ContactInformationData {
  lender: ContactInfo;
  mortgageBroker?: ContactInfo;
  realEstateBrokerBorrower?: ContactInfo;
  realEstateBrokerSeller?: ContactInfo;
  settlementAgent: ContactInfo;
}

export interface CDContactInformationProps {
  /** Contact information data */
  data: Partial<ContactInformationData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<ContactInformationData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDContactInformation - Contact Information table on Page 5
 *
 * Shows contact details for all parties involved in the transaction.
 */
export const CDContactInformation = React.forwardRef<HTMLDivElement, CDContactInformationProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    // Helper to update a contact's field
    const updateContact = (
      contactKey: keyof ContactInformationData,
      field: keyof ContactInfo,
      value: string,
    ) => {
      const contact = data[contactKey] || {};
      onChange?.({
        ...data,
        [contactKey]: { ...contact, [field]: value },
      });
    };

    const renderContactColumn = (
      contact: ContactInfo | undefined,
      header: string,
      isFirst: boolean,
      contactKey?: keyof ContactInformationData,
    ) => (
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'bg-cd-gray-header border border-cd-black px-1 py-0.5 cd-text-tiny font-bold text-center',
            !isFirst && 'border-l-0',
          )}
        >
          {header}
        </div>
        <div className={cn('border border-t-0 border-cd-black', !isFirst && 'border-l-0')}>
          {/* Name */}
          <div className="border-b border-cd-black px-1 py-0.5">
            <div className="cd-text-tiny text-cd-gray-text">Name</div>
            <div className="cd-text-tiny font-bold min-h-[1rem]">
              <CDEditableText
                value={contact?.name || ''}
                editable={editable && !!contactKey}
                onChange={(v) => contactKey && updateContact(contactKey, 'name', v)}
                size="tiny"
                bold
              />
            </div>
          </div>
          {/* Address */}
          <div className="border-b border-cd-black px-1 py-0.5">
            <div className="cd-text-tiny text-cd-gray-text">Address</div>
            <div className="cd-text-tiny whitespace-pre-line min-h-[2rem]">
              <CDEditableText
                value={contact?.address || ''}
                editable={editable && !!contactKey}
                onChange={(v) => contactKey && updateContact(contactKey, 'address', v)}
                size="tiny"
              />
            </div>
          </div>
          {/* NMLS ID */}
          <div className="border-b border-cd-black px-1 py-0.5">
            <div className="cd-text-tiny text-cd-gray-text">NMLS ID</div>
            <div className="cd-text-tiny min-h-[1rem]">
              <CDEditableText
                value={contact?.nmls || ''}
                editable={editable && !!contactKey}
                onChange={(v) => contactKey && updateContact(contactKey, 'nmls', v)}
                size="tiny"
              />
            </div>
          </div>
          {/* State License ID */}
          <div className="border-b border-cd-black px-1 py-0.5">
            <div className="cd-text-tiny text-cd-gray-text">State License ID</div>
            <div className="cd-text-tiny min-h-[1rem]">
              <CDEditableText
                value={contact?.stateLicense || ''}
                editable={editable && !!contactKey}
                onChange={(v) => contactKey && updateContact(contactKey, 'stateLicense', v)}
                size="tiny"
              />
            </div>
          </div>
          {/* Contact */}
          <div className="border-b border-cd-black px-1 py-0.5">
            <div className="cd-text-tiny text-cd-gray-text">Contact</div>
            <div className="cd-text-tiny min-h-[1rem]">
              <CDEditableText
                value={contact?.contactName || ''}
                editable={editable && !!contactKey}
                onChange={(v) => contactKey && updateContact(contactKey, 'contactName', v)}
                size="tiny"
              />
            </div>
          </div>
          {/* Contact NMLS ID */}
          <div className="border-b border-cd-black px-1 py-0.5">
            <div className="cd-text-tiny text-cd-gray-text">Contact NMLS ID</div>
            <div className="cd-text-tiny min-h-[1rem]">
              <CDEditableText
                value={contact?.contactNmls || ''}
                editable={editable && !!contactKey}
                onChange={(v) => contactKey && updateContact(contactKey, 'contactNmls', v)}
                size="tiny"
              />
            </div>
          </div>
          {/* Contact State License ID */}
          <div className="border-b border-cd-black px-1 py-0.5">
            <div className="cd-text-tiny text-cd-gray-text">Contact State License ID</div>
            <div className="cd-text-tiny min-h-[1rem]">
              <CDEditableText
                value={contact?.contactStateLicense || ''}
                editable={editable && !!contactKey}
                onChange={(v) => contactKey && updateContact(contactKey, 'contactStateLicense', v)}
                size="tiny"
              />
            </div>
          </div>
          {/* Email */}
          <div className="border-b border-cd-black px-1 py-0.5">
            <div className="cd-text-tiny text-cd-gray-text">Email</div>
            <div className="cd-text-tiny min-h-[1rem]">
              <CDEditableText
                value={contact?.email || ''}
                editable={editable && !!contactKey}
                onChange={(v) => contactKey && updateContact(contactKey, 'email', v)}
                size="tiny"
              />
            </div>
          </div>
          {/* Phone */}
          <div className="px-1 py-0.5">
            <div className="cd-text-tiny text-cd-gray-text">Phone</div>
            <div className="cd-text-tiny min-h-[1rem]">
              <CDEditableText
                value={contact?.phone || ''}
                editable={editable && !!contactKey}
                onChange={(v) => contactKey && updateContact(contactKey, 'phone', v)}
                size="tiny"
              />
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Contact Information" />

        <div className="flex">
          {renderContactColumn(data.lender, 'Lender', true, 'lender')}
          {data.mortgageBroker &&
            renderContactColumn(data.mortgageBroker, 'Mortgage Broker', false, 'mortgageBroker')}
          {renderContactColumn(
            data.realEstateBrokerBorrower,
            'Real Estate Broker (B)',
            false,
            'realEstateBrokerBorrower',
          )}
          {renderContactColumn(
            data.realEstateBrokerSeller,
            'Real Estate Broker (S)',
            false,
            'realEstateBrokerSeller',
          )}
          {renderContactColumn(data.settlementAgent, 'Settlement Agent', false, 'settlementAgent')}
        </div>
      </div>
    );
  },
);

CDContactInformation.displayName = 'CDContactInformation';
